import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Dialog, Typography } from '@nutrien/cxp-components';
import React, { useState } from 'react';

import { deleteTask } from '../../graphql';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import { useRxDB } from '../../rxdb/useRxDB';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { useNotification } from '../../utilities/useNotification';

interface Props {
  open: boolean;
  onClose: () => void;
  taskData: TaskList | number[];
  isInspectorDeletion: boolean;
  isTaskDeletion: boolean;
  onDeleteInspector?: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    deleteGrid: { padding: '25px 0px 25px 0px' },
    deleteText: { width: '100%', letterSpacing: '0.15px' },
    onlineDialogGrid: {
      padding: 5,
      '& .deleteTaskbutton': {
        background: '#CD4F5F',
      },
    },
  }),
);

const DeleteTask: React.FC<Props> = ({
  open,
  onClose,
  taskData,
  isInspectorDeletion,
  isTaskDeletion,
  onDeleteInspector,
}) => {
  const { successNotification, errorNotification } = useNotification();
  const online = useOnlineStatus();
  const classes = useStyles();
  const { db } = useRxDB();
  const [isDeleting, setIsDeleting] = useState(false);
  const message = isTaskDeletion ? 'Task ID ' : 'Inspection ID ';

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteTask(taskData as TaskList);
      if (result) {
        successNotification('Task is Deleted Successfully');
        onClose();
        db?.startReplication();
      }
    } catch (error) {
      errorNotification('Task is Not Deleted');
      // eslint-disable-next-line no-console
      console.log('🚀 ~ file: DeleteTask.ts ~ line 40 ~ confirmDelete ~ error', error);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: DeleteTask.tsx ~ line 40 ~ confirmDelete ~',
        'deleteTask',
        JSON.stringify(taskData),
      );
    }
    setIsDeleting(false);
  };

  const MultipleTaskMessage = (val: number | TaskList) => {
    return val ? (
      <>
        {'Are you sure want to delete the '}
        <b>{message + val}</b>
        {' from this inspection?'}
      </>
    ) : (
      <>
        {'Are you sure want to delete the '}
        <b>
          {message +
            (taskData as number[])?.map((i: number) => {
              return `${i}  `;
            })}
        </b>
        {' from this inspection?'}
      </>
    );
  };

  const deletedInspectors = () => {
    setIsDeleting(true);
    onDeleteInspector?.();
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      titleText={isTaskDeletion ? 'Delete Task' : 'Delete Inspection'}
      backButton={false}
      dialogActions={
        online ? (
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            className={classes.onlineDialogGrid}
          >
            <Grid item>
              <Button
                noMinHeight
                variant="contained"
                onClick={isInspectorDeletion ? deletedInspectors : confirmDelete}
                isLoading={isDeleting}
                className="deleteTaskbutton"
                data-testid="deleteTask"
              >
                Yes, Delete
              </Button>
            </Grid>
          </Grid>
        ) : (
          <></>
        )
      }
      maxWidth="xs"
      noMinHeight
    >
      <Grid container className={classes.deleteGrid}>
        <Grid item container xs={12} alignItems="center">
          <Typography className={classes.deleteText}>
            {online
              ? !isInspectorDeletion
                ? MultipleTaskMessage(
                    isTaskDeletion
                      ? (taskData as TaskList)?.pkAssetInspectionTask
                      : (taskData as TaskList)?.fkAssetInspection,
                  )
                : (taskData as unknown[])?.length === 1
                ? MultipleTaskMessage(taskData as TaskList)
                : MultipleTaskMessage(0)
              : `User Need To Connected With Internet To Delete Task`}
          </Typography>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default DeleteTask;
